import { Done as DoneIcon, HelpOutline as HelpOutlineIcon } from "@mui/icons-material";
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Box, Button, Card,
  CardActions,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip, Typography
} from "@mui/material";
import { apiRequest } from "api-client";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from "react-toastify";
import { getFileNameFromUrl } from "utils/url";
// @ts-ignore
import "video.js/dist/video-js.css";

export interface ImageUploadProps {
  label: string;
  disabled?: boolean;
  path?: string;
  sourceUrl?: string;
  fileName?: string;
  uploadedUrl?: string;
  onUploaded: (url: string) => void;
}

/**
 * This component will support many user cases in this app.
 */
export const ImageUpload = ({
  label,
  disabled,
  path, // server local path or cdn path
  sourceUrl, // file remote url instead of a local file
  fileName,  // the uploaded file name
  uploadedUrl, // full url to the uploaded file
  /** the callback will output only file name of having path param, otherwise it will output the full url */
  onUploaded,
}: ImageUploadProps) => {
  const [file, setFile] = useState<File>();
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  // use file input in case no remote source url
  const [showFileInput, setShowFileInput] = useState(true);

  useEffect(() => {
    setShowFileInput(!sourceUrl);
  }, [sourceUrl]);

  const inputRef = useRef<HTMLInputElement>(null);

  const previewUrl = useMemo(() => {
    if (uploadedUrl) {
      return uploadedUrl;
    }
    if (fileName) {
      return [process.env.REACT_APP_CDN_DOMAIN, path, fileName].join('/');
    }
    if (sourceUrl) {
      return sourceUrl;
    }
    return '';
  }, [sourceUrl, fileName, uploadedUrl, path]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files ? e.target.files[0] : undefined);
  }

  const onUploadLocalFile = async () => {
    if (!file) {
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append('file', file, file.name);

    if (path) {
      form.append('path', path);
    }

    try {
      const data = await apiRequest.post(`/files`, form);
      setFile(undefined);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      setUploaded(true);
      /**
       * With path, the output should only the file name and consumer will generate full URL itself
       * Without path, the output should be the full url.
       */
      onUploaded(path ? getFileNameFromUrl(data.url) : data.url);
      setUploading(false);
    } catch (e: any) {
      toast.error(e.message || 'Something went wrong');
      setUploading(false);
    }
  }

  const uploadRemoteFile = async () => {
    setUploading(true);

    try {
      const data = await apiRequest.post(`/files`, {
        path,
        sourceUrl,
      });
      onUploaded(path ? getFileNameFromUrl(data.url) : data.url);
      setUploaded(true);
      setUploading(false);
    } catch (e: any) {
      toast.error(e.message || 'Something went wrong');
      setUploading(false);
    }
  }


  return (
    <>
      <Card variant="outlined">
        {!!previewUrl &&
          <CardMedia
            component={'img'}
            src={previewUrl}
          />
        }
        {!!sourceUrl && !uploaded &&
          <Alert color="warning">This is the Preview Image from remote file. Click Upload to save it to server.</Alert>
        }

        <CardContent>
          <Typography gutterBottom variant="h5" component="h2">
            {label}
            <Tooltip title="Please select an image with aspect ratio 16:9 and compress it before uploading. The image size should be less than 2MB.">
              <IconButton size="small">
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          </Typography>
          {!showFileInput &&
            <Typography>Upload a local file instead?
              <Button className="ml-1" variant="text" onClick={() => setShowFileInput(true)}>Click here</Button>
            </Typography>
          }
        </CardContent>
        <CardActions>
          {showFileInput &&
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <input ref={inputRef} type="file" accept="image/*" onChange={onChange} disabled={disabled} />
              {!!file &&
                <LoadingButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={onUploadLocalFile}
                  disabled={disabled}
                  loading={uploading}
                >
                  Upload
                </LoadingButton>
              }
            </Box>
          }
          {!!sourceUrl && !showFileInput &&
            <>
              {!!sourceUrl &&
                <Button variant="text">
                  <a href={sourceUrl} target="_blank" rel="noopener noreferrer">View remote file</a>
                </Button>
              }
              <LoadingButton
                variant="outlined"
                color="primary"
                size="small"
                onClick={uploadRemoteFile}
                disabled={disabled}
                loading={uploading}
              >
                Upload
              </LoadingButton>
            </>
          }
          {uploaded &&
            <DoneIcon color="success" />
          }
        </CardActions>
      </Card>
    </>
  );
};